import React, { ChangeEvent, memo, useState } from 'react'
import omit from 'lodash/omit'
import { Button } from 'transfix-ui/components/Button'
import { Facet } from 'transfix-ui/components/Facet'
import { ISuggestResult } from 'transfix-ui/components/FilterAutocomplete'
import { IFilterMultiSelectOption } from 'transfix-ui/components/FilterMultiSelect'
import { Box } from 'transfix-ui/core'
import { useDebouncedCallback } from 'use-debounce'
import { ShipperRatesFilters } from 'apps/marketplace/apiTypes/shipperRates'
import AdminFilterAutocomplete from 'apps/marketplace/components/AdminFilterAutocomplete'
import CarrierFilterAutocomplete from 'apps/marketplace/components/CarrierFilterAutocomplete'
import FilterMultiSelectDisplay from 'apps/marketplace/components/FilterMultiSelectDisplay'
import ShipperFilterAutocomplete from 'apps/marketplace/components/ShipperFilterAutocomplete'
import {
  jobStatusMultiSelectOptions,
  dropTypeMultiSelectOptions,
} from 'apps/marketplace/utils/constants/shipperRates'
import { TextInput } from 'components/atoms'
import {
  DryVanIcon,
  RoutingGuideIcon,
  LocationPinIcon,
  LaneArrowIcon,
  UserIcon,
} from 'transfix-icons'
import { ButtonGroup } from 'transfix-ui'
import { carrierSalesRegions } from 'constants/countriesStates'
import { equipmentOptions } from 'constants/equipmentConstants'
import LaneLocationSearch, {
  ILaneLocationSearchValues,
} from '../../lane/assignment/LaneLocationSearch'

type IFilterAutocompleteProp = string | ISuggestResult | (string | ISuggestResult)[] | null
type ILocationSearchType = 'dat_markets' | 'city' | 'award_id'
interface ILanesFilterProps {
  filters: ShipperRatesFilters
  setFilters: (updatedParams: ShipperRatesFilters) => void
  clearFilters: () => void
}

const AwardedLanesFilters = memo<ILanesFilterProps>(({ clearFilters, filters, setFilters }) => {
  const [toggleClear, setToggleClear] = useState<boolean>(false)
  const [searchType, setSearchType] = useState<ILocationSearchType>('dat_markets')
  const regionOptions: IFilterMultiSelectOption[] = carrierSalesRegions()

  const {
    assignedToMe,
    deliveryCity,
    deliveryRadius,
    deliverySearchType,
    deliveryStateCode,
    dropType,
    equipmentId,
    pickupCity,
    pickupRadius,
    pickupRegion,
    pickupSearchType,
    pickupStateCode,
    shipperRateId,
    sourceStatus,
  } = filters || {}

  const [setFilterValue] = useDebouncedCallback((updatedFilters: ShipperRatesFilters) => {
    setFilters({ ...filters, ...updatedFilters })
  }, 250)

  const facetHandler = (value: boolean, filterName?: string) => {
    if (!filterName) return
    if (value) {
      setFilterValue({ [filterName]: value })
    } else {
      setFilters(omit(filters, [filterName]))
    }
  }

  const isAutosuggestResult = (autocompleteObj: any): autocompleteObj is ISuggestResult => {
    return autocompleteObj && !Array.isArray(autocompleteObj) && typeof autocompleteObj === 'object'
  }

  const autocompleteFilters = (autocompleteObj: IFilterAutocompleteProp, key: string) => {
    if (isAutosuggestResult(autocompleteObj)) {
      setFilterValue({ [key]: autocompleteObj.id })
    } else {
      setFilters(omit(filters, [key]))
    }
  }

  const searchLocationHandler = (field: string, value?: ILaneLocationSearchValues) => {
    const isPickup = field === 'pickup'
    if (value) {
      const { city, stateCode } = value || {}
      const updateFields = isPickup
        ? { pickupCity: city, pickupStateCode: stateCode, pickupSearchType: searchType }
        : { deliveryCity: city, deliveryStateCode: stateCode, deliverySearchType: searchType }
      setFilterValue(updateFields)
    } else {
      const omitFields = isPickup
        ? ['pickupCity', 'pickupStateCode', 'pickupSearchType']
        : ['deliveryCity', 'deliveryStateCode', 'deliverySearchType']
      setFilters(omit(filters, omitFields))
    }
  }

  const shipperAutoComplete = (e: IFilterAutocompleteProp) =>
    autocompleteFilters(e, 'shipperApplicationId')
  const carrierAutoComplete = (e: IFilterAutocompleteProp) =>
    autocompleteFilters(e, 'carrierApplicationId')
  const opsRepAutocomplete = (e: IFilterAutocompleteProp) =>
    autocompleteFilters(e, 'carrierSalesRepId')

  const setMultiSelectHandler = (results?: string[], id?: string) => {
    if (id) {
      if (results && results.length > 0) {
        setFilterValue({ [id]: results })
      } else {
        setFilters(omit(filters, [id]))
      }
    }
  }

  const clearAll = () => {
    setToggleClear(!toggleClear)
    clearFilters()
  }

  const onSetSearchType = (type: string) => {
    setSearchType(type as ILocationSearchType)
    if (pickupCity || pickupStateCode) setFilterValue({ pickupSearchType: type })
    if (deliveryCity || deliveryStateCode) setFilterValue({ deliverySearchType: type })
    if (type === 'dat_markets')
      setFilters(omit(filters, ['pickupRadius', 'deliveryRadius', 'shipperRateId']))
    if (type === 'city') setFilters(omit(filters, ['shipperRateId']))
    if (type === 'award_id')
      setFilters(
        omit(filters, [
          'pickupCity',
          'pickupStateCode',
          'pickupRadius',
          'pickupSearchType',
          'deliveryCity',
          'deliveryStateCode',
          'deliverySearchType',
          'deliveryRadius',
        ])
      )
  }

  const onSetPickupRadiusType = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setFilterValue({ pickupRadius: Number(e.target.value) })
    } else {
      setFilters(omit(filters, 'pickupRadius'))
    }
  }

  const onSetDeliveryRadiusType = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setFilterValue({ deliveryRadius: Number(e.target.value) })
    } else {
      setFilters(omit(filters, 'deliveryRadius'))
    }
  }

  const onSetShipperRateId = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setFilterValue({ shipperRateId: Number(e.target.value) })
    } else {
      setFilters(omit(filters, 'shipperRateId'))
    }
  }

  const isCity = searchType === 'city'

  return (
    <>
      <Box display='flex' alignItems='flex-end' mb={2} whiteSpace='nowrap'>
        <Box display='flex' alignItems='center' mr={1}>
          <ButtonGroup
            value={searchType || pickupSearchType || deliverySearchType}
            hideLabel
            onChange={onSetSearchType}
            dataTest='search-type'>
            <Button value='dat_markets' size='large' data-test='dat-market-search'>
              Market Search
            </Button>
            <Button value='city' size='large' data-test='city-search'>
              City Search
            </Button>
            <Button value='award_id' size='large' data-test='award-id-search'>
              Award ID
            </Button>
          </ButtonGroup>
        </Box>
        <Box display='flex' alignItems='center' width='70%'>
          {searchType === 'award_id' ? (
            <Box width='40%'>
              <TextInput
                name='award-id'
                type='number'
                large
                placeholder='Award ID'
                value={shipperRateId}
                dataTest='award-id-filter'
                onChange={onSetShipperRateId}
              />
            </Box>
          ) : (
            <>
              <Box display='flex' width='45%'>
                <Box width={isCity ? '80%' : '100%'}>
                  <LaneLocationSearch
                    placeholder='Search Pickup Area'
                    id='pickup'
                    dataTest='search-pickup'
                    onChange={searchLocationHandler}
                    hasValue={Boolean(filters?.pickupCity || filters?.pickupStateCode)}
                  />
                </Box>
                {isCity && (
                  <Box width='20%' ml={1}>
                    <TextInput
                      name='pickup-radius'
                      type='number'
                      large
                      placeholder='Radius'
                      value={pickupRadius}
                      dataTest='pickup-radius-filter'
                      onChange={onSetPickupRadiusType}
                    />
                  </Box>
                )}
              </Box>
              <LaneArrowIcon className='mh2' />
              <Box display='flex' width='45%'>
                <Box width={isCity ? '80%' : '100%'}>
                  <LaneLocationSearch
                    placeholder='Search Delivery Area'
                    id='delivery'
                    dataTest='search-delivery'
                    onChange={searchLocationHandler}
                    hasValue={Boolean(filters?.deliveryCity || filters?.deliveryStateCode)}
                  />
                </Box>
                {isCity && (
                  <Box width='20%' ml={1}>
                    <TextInput
                      name='delivery-radius'
                      type='number'
                      large
                      placeholder='Radius'
                      value={deliveryRadius}
                      dataTest='pickup-radius-filter'
                      onChange={onSetDeliveryRadiusType}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box display='flex' alignItems='flex-end'>
        <Facet
          id='assignedToMe'
          dataTest='assigned-to-me-facet'
          onClick={facetHandler}
          label='Assigned to Me'
          icon={<UserIcon size='large' />}
          selected={Boolean(assignedToMe)}
          isLoading={false}
        />
        <Box width='auto' ml={1}>
          <AdminFilterAutocomplete
            label='Carrier Sales Rep'
            onSelectedValue={opsRepAutocomplete}
            toggleClear={toggleClear}
            userType='transfix_admin'
          />
        </Box>
        <Box width='auto' ml={1}>
          <FilterMultiSelectDisplay
            label='Job Status'
            dataTest='job-status-filter'
            id='sourceStatus'
            onSelectHandler={setMultiSelectHandler}
            hasValues={Boolean(sourceStatus)}
            icon={<RoutingGuideIcon size='large' />}
            options={jobStatusMultiSelectOptions(sourceStatus)}
          />
        </Box>
        <Box width='auto' ml={1}>
          <ShipperFilterAutocomplete
            label='Shipper'
            onSelectedValue={shipperAutoComplete}
            toggleClear={toggleClear}
          />
        </Box>
        <Box width='auto' ml={1}>
          <CarrierFilterAutocomplete
            label='Carrier'
            onSelectedValue={carrierAutoComplete}
            toggleClear={toggleClear}
          />
        </Box>
        <Box width='auto' ml={1}>
          <FilterMultiSelectDisplay
            label='Pickup Region'
            dataTest='select-pickup-region'
            id='pickupRegion'
            onSelectHandler={setMultiSelectHandler}
            hasValues={Boolean(pickupRegion)}
            icon={<LocationPinIcon size='large' />}
            options={regionOptions}
          />
        </Box>
        <Box width='auto' ml={1}>
          <FilterMultiSelectDisplay
            label='Loading Method'
            dataTest='loading-method-filter'
            id='dropType'
            onSelectHandler={setMultiSelectHandler}
            hasValues={Boolean(dropType)}
            icon={<DryVanIcon size='large' />}
            options={dropTypeMultiSelectOptions}
          />
        </Box>
        <Box width='auto' ml={1}>
          <FilterMultiSelectDisplay
            label='Equipment Type'
            dataTest='select-equipment'
            id='equipmentId'
            onSelectHandler={setMultiSelectHandler}
            hasValues={Boolean(equipmentId)}
            icon={<DryVanIcon size='large' />}
            options={equipmentOptions}
          />
        </Box>
        <Box width='auto' ml={1} whiteSpace='nowrap'>
          <Button className='btn-tertiary' onClick={clearAll}>
            Clear All
          </Button>
        </Box>
      </Box>
    </>
  )
})

export default AwardedLanesFilters
